import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, Clock3, FileText, Package, Plus, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader } from '@/components/app/PageHeader';
import { EmptyState, PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { EditedMark, ExampleDataNote, LinkStat, TimeStatusPill, useDriftRefs } from '@/components/app/DriftUI';
import { DriftFilterBar, useDriftFilters } from '@/components/app/DriftFilters';
import { DeviationList, MeldAvvikSheet } from '@/components/app/Avvikssaker';
import { DRIFT_REPORTS, byUnitText, getProject, hoursText, listDeviations } from '@/lib/driftApi';
import { formatDateShort } from '@/domain/time';
import { fmtNumber } from '@/domain/numbers';
import { ProjectStatusPill } from '@/features/ProsjekterPage';
import { cn } from '@/lib/utils';

/*
  Prosjektdetalj: ett sted for alt som er ført på prosjektet i valgt periode.
  Arbeidstimer og maskintimer vises hver for seg, tillegg per type (egen enhet), vareforbruk per vare + enhet.
  Hvert tall lenker til registreringene bak. Registrering fra prosjektet starter med prosjektet forhåndsvalgt.
*/

const NO_KEYS = [];

/** HMS/RUH/kvalitet for prosjektet: åpne saker først, uavhengig av periodefilteret (en åpen sak er relevant til den er lukket). */
function ProjectDeviationsPanel({ project }) {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const ny = params.get('meldavvik');
  const load = useCallback(async () => {
    try {
      setData(await listDeviations({ projectId: project.id, limit: 500 }));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [project.id]);
  useEffect(() => {
    load();
  }, [load]);
  const setNy = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set('meldavvik', v);
    else next.delete('meldavvik');
    setParams(next, { replace: true });
  };
  const here = { label: 'prosjektet', to: `${location.pathname}${location.search}` };
  const all = data?.deviations ?? [];
  const open = all.filter((d) => d.open);
  const s = data?.summary;
  const listLink = `/avvik?fane=hms&projectId=${encodeURIComponent(project.id)}&visning=alle`;
  return (
    <Panel data-testid="prosjekt-deviations">
      <PanelHeader
        title="HMS / RUH / Kvalitet"
        description={s ? `${s.open} åpne · ${s.closed} lukket${s.overdue ? ` · ${s.overdue} med passert frist` : ''}${s.actionsOpen ? ` · ${s.actionsOpen} åpne tiltak` : ''}` : 'Manuelt meldte avvik knyttet til prosjektet.'}
        actions={
          <Button size="sm" className="h-8 rounded-[8px] bg-[var(--hr-heading)] px-3 text-[13px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" onClick={() => setNy('1')} data-testid="prosjekt-new-deviation">
            <ShieldAlert className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Meld avvik
          </Button>
        }
      />
      {status === 'loading' && !data && <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Henter avvik …</p>}
      {status === 'error' && !data && (
        <p className="px-5 py-4 text-[14px] text-[var(--hr-amber)]" role="alert" data-testid="prosjekt-deviations-error">
          Kunne ikke hente avvikene.{' '}
          <button type="button" onClick={load} className="font-medium underline underline-offset-2">Prøv igjen</button>
        </p>
      )}
      {data && (
        <>
          <DeviationList deviations={open} showProject={false} compact limit={5} testId="prosjekt-dev" from={here} emptyText={all.length ? 'Ingen åpne avvik på prosjektet.' : 'Ingen avvik meldt på prosjektet.'} />
          <div className="flex items-center justify-between gap-2 border-t border-[var(--hr-hairline)] px-5 py-2.5 text-[13px]">
            <span className="text-[var(--hr-muted)]">{open.length ? `${open.length} åpne vist` : ''}</span>
            <Link to={listLink} state={{ from: here }} className="inline-flex items-center gap-1 font-medium text-[var(--hr-accent-text)] hover:underline" data-testid="prosjekt-all-deviations">
              Alle avvik for prosjektet ({all.length}) <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>
        </>
      )}
      <MeldAvvikSheet open={!!ny} onOpenChange={(o) => !o && setNy(null)} initialProjectId={project.id} onSaved={load} returnTo={here} />
    </Panel>
  );
}

function Row({ label, sublabel, value, meta, to, testId }) {
  const inner = (
    <>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-medium text-[var(--hr-heading)]">{label}</div>
        {sublabel && <div className="truncate text-[13px] text-[var(--hr-muted)]">{sublabel}</div>}
      </div>
      <div className="shrink-0 text-right">
        <div className="hr-num text-[14px] font-semibold text-[var(--hr-heading)]">{value}</div>
        {meta && <div className="hr-tabular text-[12px] text-[var(--hr-muted-2)]">{meta}</div>}
      </div>
    </>
  );
  const cls = 'flex items-center gap-3 px-5 py-2.5';
  if (!to) return <li className={cls} data-testid={testId}>{inner}</li>;
  return (
    <li>
      <Link to={to} className={cn(cls, 'transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]')} data-testid={testId}>
        {inner}
      </Link>
    </li>
  );
}

export default function ProsjektDetaljPage() {
  const { id } = useParams();
  const location = useLocation();
  const back = location.state?.from;
  const { refs } = useDriftRefs();
  const f = useDriftFilters({ keys: NO_KEYS });
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');

  const queryKey = JSON.stringify(f.query);
  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      setData(await getProject(id, JSON.parse(queryKey)));
      setStatus('ready');
    } catch (e) {
      setStatus(e?.status === 404 ? 'notfound' : 'error');
    }
  }, [id, queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (data?.project) document.title = `${data.project.name} · Prosjekter · Hardanger Rock`;
  }, [data]);

  if (status === 'loading' && !data) return <PageSkeleton />;
  if (status === 'notfound') {
    return (
      <EmptyState
        title="Prosjektet finnes ikke"
        description="Prosjektet er ukjent i eksempeldataene."
        action={
          <Button asChild size="sm">
            <Link to="/prosjekter">Til prosjektlisten</Link>
          </Button>
        }
      />
    );
  }
  if (status === 'error' && !data) {
    return (
      <Note tone="amber" data-testid="prosjekt-load-error">
        Kunne ikke hente prosjektet. <Button variant="link" className="h-auto px-1" onClick={load} data-testid="prosjekt-load-retry">Prøv igjen</Button>
      </Note>
    );
  }

  const { project: p, sums, bySubproject, byEmployee, byMachine, consumptionByItem, recentTime, recentConsumption, period } = data;
  const units = refs?.units;
  const link = (path, extra = {}) => `${path}${f.carry({ projectId: p.id, ...extra })}`;
  const supplements = sums.supplements ?? [];
  const forbrukText = byUnitText(sums.consumptionByUnit, units);
  const byCategory = consumptionByItem.reduce((acc, it) => {
    (acc[it.category] ||= []).push(it);
    return acc;
  }, {});
  const categoryLabel = (c) => refs?.categories?.find((x) => x.id === c)?.label ?? c;

  return (
    <div className="space-y-5" data-testid="prosjekt-detalj">
      {back?.to && (
        <Link to={back.to} className="inline-flex items-center gap-1.5 rounded-sm text-[14px] font-medium text-[var(--hr-muted)] transition-colors duration-150 hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="prosjekt-back-link">
          ← Tilbake til {back.label}
        </Link>
      )}
      <PageHeader title={p.name} lead={`${p.customer} · ${p.location}. ${p.description}`}>
        <Button asChild variant="outline" className="h-10 rounded-[10px] bg-white px-3 text-[14px] font-medium">
          <Link to={link('/vareforbruk', { ny: 1 })} data-testid="prosjekt-new-consumption">
            <Package className="mr-1.5 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Registrer forbruk
          </Link>
        </Button>
        <Button asChild className="h-10 rounded-[10px] bg-[var(--hr-heading)] px-4 text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]">
          <Link to={link('/timer', { ny: 1 })} data-testid="prosjekt-new-time">
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Registrer timer
          </Link>
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2">
        <ProjectStatusPill status={p.status} data-testid="prosjekt-status" />
        <span className="text-[13px] text-[var(--hr-muted)]">{p.subprojects.length ? `${p.subprojects.length} underprosjekter` : 'Ingen underprosjekter'}</span>
        {p.status === 'avsluttet' && <span className="text-[13px] text-[var(--hr-muted)]">· Avsluttet prosjekt – nye føringer bør kontrolleres</span>}
      </div>

      <ExampleDataNote>Fiktivt prosjekt. Forbrukte masser i tonn er forbruk på prosjektet – aldri produserte tonn.</ExampleDataNote>

      <Panel className="min-w-0">
        <DriftFilterBar f={f} refs={refs} showProject={false} testPrefix="prosjekt" className="border-b-0" />
      </Panel>

      <Panel>
        <PanelHeader title={`Sum for ${period.label.toLowerCase()}`} description="Arbeidstimer og maskintimer hver for seg. Tillegg per type. Vareforbruk per enhet – aldri summert på tvers." />
        <div className="grid grid-cols-2 divide-x divide-[var(--hr-hairline)] lg:grid-cols-4" data-testid="prosjekt-sums">
          <LinkStat label="Arbeidstimer" value={hoursText(sums.hours)} meta={`${sums.timeCount} ${sums.timeCount === 1 ? 'føring' : 'føringer'}${sums.byStatus.sendt ? ` · ${sums.byStatus.sendt} til godkjenning` : ''}${sums.byStatus.returnert ? ` · ${sums.byStatus.returnert} returnert` : ''}`} tone={sums.byStatus.sendt || sums.byStatus.returnert ? 'amber' : undefined} to={link('/timer')} testId="prosjekt-stat-hours" />
          <LinkStat label="Maskintimer" value={hoursText(sums.machineHours)} meta={byMachine.length ? `${byMachine.length} ${byMachine.length === 1 ? 'maskin' : 'maskiner'}` : 'Ingen maskinbruk ført'} to={link('/timer', { machineId: 'med' })} testId="prosjekt-stat-machine" />
          <LinkStat label="Tillegg" value={supplements.length ? supplements.map((s) => `${fmtNumber(s.quantity, s.unit === 'stk' ? 0 : 1)} ${s.unit}`).join(' · ') : '–'} meta={supplements.map((s) => s.label).join(' · ') || 'Ingen tillegg'} to={link('/rapporter/timer', { groupBy: 'medarbeider' })} testId="prosjekt-stat-supplements" />
          <LinkStat label="Vareforbruk" value={forbrukText ?? '–'} meta={sums.consumptionLines ? `${sums.consumptionLines} varelinjer · ${sums.consumptionCount} registreringer` : 'Ingen forbruk ført'} to={link('/vareforbruk')} testId="prosjekt-stat-consumption" />
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-12">
        <div className="min-w-0 space-y-5 lg:col-span-7">
          <Panel className="min-w-0">
            <PanelHeader title="Underprosjekter" description="Timer og forbruk per underprosjekt. Klikk for å åpne føringene." />
            {bySubproject.length === 0 ? (
              <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen føringer i perioden.</p>
            ) : (
              <div className="hr-scroll overflow-x-auto">
                <Table data-testid="prosjekt-subprojects">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Underprosjekt</TableHead>
                      <TableHead className="text-right">Arbeidstimer</TableHead>
                      <TableHead className="text-right">Maskintimer</TableHead>
                      <TableHead className="text-right">Føringer</TableHead>
                      <TableHead>Vareforbruk</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bySubproject.map((s) => (
                      <TableRow key={s.key || 'none'} data-testid={`prosjekt-sub-${s.key || 'none'}`}>
                        <TableCell className="font-medium text-[var(--hr-heading)]">
                          <Link to={link('/timer', s.key ? { subprojectId: s.key } : {})} className="hover:underline">
                            {s.label}
                          </Link>
                        </TableCell>
                        <TableCell className="hr-num text-right font-semibold">{hoursText(s.hours)}</TableCell>
                        <TableCell className="hr-num text-right">{s.machineHours ? hoursText(s.machineHours) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
                        <TableCell className="hr-tabular text-right">
                          {s.count}
                          {s.pending > 0 && <span className="ml-1 text-[12px] text-[var(--hr-amber)]">({s.pending} til godkj.)</span>}
                        </TableCell>
                        <TableCell className="hr-tabular text-[13px]">
                          {s.consumption?.lines ? (
                            <Link to={link('/vareforbruk', s.key ? { subprojectId: s.key } : {})} className="hover:underline">
                              {byUnitText(s.consumption.byUnit, units)}
                            </Link>
                          ) : (
                            <span className="text-[var(--hr-muted-2)]">–</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Panel>

          <div className="grid min-w-0 gap-5 md:grid-cols-2 [&>*]:min-w-0">
            <Panel>
              <PanelHeader title="Per medarbeider" />
              {byEmployee.length === 0 ? <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen føringer.</p> : (
                <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="prosjekt-by-employee">
                  {byEmployee.map((e) => (
                    <Row key={e.key} label={e.label} sublabel={e.role} value={hoursText(e.hours)} meta={e.machineHours ? `${hoursText(e.machineHours)} maskin` : null} to={link('/timer', { employeeId: e.key })} testId={`prosjekt-emp-${e.key}`} />
                  ))}
                </ul>
              )}
            </Panel>
            <Panel>
              <PanelHeader title="Maskinbruk" description="Registrert maskintid fra timeføring." />
              {byMachine.length === 0 ? <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen maskinbruk ført.</p> : (
                <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="prosjekt-by-machine">
                  {byMachine.map((m) => (
                    <Row key={m.key} label={m.label} value={hoursText(m.machineHours)} meta={`${m.count} ${m.count === 1 ? 'føring' : 'føringer'}`} to={link('/timer', { machineId: m.key })} testId={`prosjekt-machine-${m.key}`} />
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </div>

        <div className="min-w-0 space-y-5 lg:col-span-5">
          <ProjectDeviationsPanel project={p} />

          <Panel>
            <PanelHeader title="Vareforbruk per vare" description="Enheten følger varen." />
            {consumptionByItem.length === 0 ? (
              <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen forbruk ført i perioden.</p>
            ) : (
              <div className="divide-y divide-[var(--hr-hairline)]" data-testid="prosjekt-by-item">
                {Object.entries(byCategory).map(([cat, items]) => (
                  <div key={cat} className="py-1">
                    <div className="px-5 pb-1 pt-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">{categoryLabel(cat)}</div>
                    <ul>
                      {items.map((it) => (
                        <Row key={it.itemId} label={it.name} value={`${fmtNumber(it.quantity, it.unit === 'stk' ? 0 : 1)} ${units?.[it.unit] ?? it.unit}`} meta={`${it.count} ${it.count === 1 ? 'linje' : 'linjer'}`} to={link('/vareforbruk', { itemId: it.itemId })} testId={`prosjekt-item-${it.itemId}`} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel>
            <PanelHeader title="Rapporter for prosjektet" />
            <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="prosjekt-reports">
              {DRIFT_REPORTS.map((r) => (
                <li key={r.id}>
                  <Link to={link(`/rapporter/${r.id}`)} className="flex items-center gap-3 px-5 py-2.5 text-[14px] transition-colors duration-150 hover:bg-[var(--hr-surface-2)]" data-testid={`prosjekt-report-${r.id}`}>
                    <FileText className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate font-medium text-[var(--hr-heading)]">{r.label}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>

      <div className="grid min-w-0 gap-5 lg:grid-cols-2 [&>*]:min-w-0">
        <Panel>
          <PanelHeader title="Siste timeføringer" actions={<Button asChild variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px]"><Link to={link('/timer')} data-testid="prosjekt-all-time">Alle føringer</Link></Button>} />
          {recentTime.length === 0 ? <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen føringer i perioden.</p> : (
            <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="prosjekt-recent-time">
              {recentTime.map((e) => (
                <li key={e.id}>
                  <Link to={link('/timer', { rediger: e.id })} className="flex items-center gap-3 px-5 py-2.5 transition-colors duration-150 hover:bg-[var(--hr-surface-2)]" data-testid={`prosjekt-time-${e.id}`}>
                    <Clock3 className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-medium text-[var(--hr-heading)]">{e.employee?.name} · {e.activity?.label}</div>
                      <div className="hr-tabular truncate text-[13px] text-[var(--hr-muted)]">{formatDateShort(e.date)} · {e.fromTime}–{e.toTime}{e.subproject ? ` · ${e.subproject.name}` : ''}{e.machine ? ` · ${e.machine.internalNo} ${hoursText(e.machineTotalHours)}` : ''}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {e.edited && <EditedMark />}
                      <span className="hr-num text-[14px] font-semibold text-[var(--hr-heading)]">{hoursText(e.hours)}</span>
                      <TimeStatusPill status={e.status} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel>
          <PanelHeader title="Siste vareforbruk" actions={<Button asChild variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px]"><Link to={link('/vareforbruk')} data-testid="prosjekt-all-consumption">Alt forbruk</Link></Button>} />
          {recentConsumption.length === 0 ? <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen forbruk i perioden.</p> : (
            <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="prosjekt-recent-consumption">
              {recentConsumption.map((c) => (
                <li key={c.id}>
                  <Link to={link('/vareforbruk', { rediger: c.id })} className="flex items-start gap-3 px-5 py-2.5 transition-colors duration-150 hover:bg-[var(--hr-surface-2)]" data-testid={`prosjekt-cons-${c.id}`}>
                    <Package className="mt-0.5 h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="hr-tabular text-[13px] text-[var(--hr-muted)]">{formatDateShort(c.date)}{c.subproject ? ` · ${c.subproject.name}` : ''}{c.employee ? ` · ${c.employee.name}` : ''}</div>
                      <div className="truncate text-[14px] text-[var(--hr-text)]">{c.lines.map((l) => `${l.item?.name} ${fmtNumber(l.quantity, l.unit === 'stk' ? 0 : 1)} ${units?.[l.unit] ?? l.unit}`).join(' · ')}</div>
                    </div>
                    {c.edited && <EditedMark className="shrink-0" />}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
